import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { fineGrainPermission } from "src/features/fields/services/openfga";

interface PermissionProps {
  module: string;
  action: string;
}

interface TupleKey {
  user: string;
  object: string;
}

// Relation-aware permission check for a single OpenFGA object.
//
// The admin Access Manager grants View/Edit on the SAME object key:
//   View  -> relation "reader"
//   Edit  -> relations "writer" + "editor"
// The plain `usePermission` hook only reports whether ANY tuple exists, so it
// cannot tell view from edit. This hook reads the tuples back and inspects their
// relations, returning granular `{ canView, canEdit }`. Edit implies view.
const useGranularPermission = ({ module, action }: PermissionProps) => {
  const [canView, setCanView] = useState<boolean>(false);
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const { idTokenDetails } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (!module && !action) {
      setCanView(true);
      setCanEdit(true);
      setLoading(false);
      return;
    }

    const checkPermission = async () => {
      setLoading(true);
      try {
        const mv_role = idTokenDetails?.["custom:mv_role"];
        const cleanedMvRole = mv_role.replace(/[^a-zA-Z0-9]/g, "");

        const tupleKey: TupleKey = {
          user: `staff_position:${cleanedMvRole}`,
          object: `${module}:${action}`,
        };

        const response = await fineGrainPermission(tupleKey);
        if (response.status != 200) {
          throw new Error("Failed to check permission");
        }

        const tuples: any[] = response.data?.tuples || [];
        const hasEdit = tuples.some((tuple) => ["writer", "editor"].includes(tuple?.key?.relation));

        setCanEdit(hasEdit);
        // Edit grants imply the ability to view; otherwise any tuple (i.e. reader) means view.
        setCanView(hasEdit || tuples.length > 0);
      } catch (err) {
        setCanView(false);
        setCanEdit(false);
      } finally {
        setLoading(false);
      }
    };

    checkPermission();
  }, [module, action]);

  return { canView, canEdit, loading };
};

export default useGranularPermission;
